import { create } from 'zustand';
import { cartService } from '@services/catalogService';
import { getErrorMessage } from '@services/api';
import type { Cart, CartItem } from '@/types';

interface CartState extends Cart {
  isOpen: boolean;
  isLoading: boolean;
  isSyncing: boolean;
  error: string | null;
  openDrawer: () => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;
  addItem: (productId: string, quantity?: number) => Promise<void>;
  updateItem: (itemId: string, quantity: number) => Promise<void>;
  removeItem: (itemId: string) => Promise<void>;
  clearCart: () => Promise<void>;
  syncFromBackend: () => Promise<void>;
  reset: () => void;
}

const emptyCart: Cart = {
  items: [],
  subtotal: 0,
  deliveryFee: 0,
  discountTotal: 0,
  total: 0,
};

export const useCartStore = create<CartState>((set, get) => ({
  ...emptyCart,
  isOpen: false,
  isLoading: false,
  isSyncing: false,
  error: null,

  openDrawer: () => set({ isOpen: true }),
  closeDrawer: () => set({ isOpen: false }),
  toggleDrawer: () => set((s) => ({ isOpen: !s.isOpen })),

  addItem: async (productId, quantity = 1) => {
    set({ isSyncing: true, error: null });
    try {
      const cart = await cartService.addItem(productId, quantity);
      set({ ...cart, isSyncing: false, isOpen: true });
    } catch (error) {
      set({ isSyncing: false, error: getErrorMessage(error, 'Sepete eklenemedi') });
      throw error;
    }
  },

  updateItem: async (itemId, quantity) => {
    if (quantity < 1) {
      await get().removeItem(itemId);
      return;
    }
    set({ isSyncing: true, error: null });
    try {
      const cart = await cartService.updateItem(itemId, quantity);
      set({ ...cart, isSyncing: false });
    } catch (error) {
      set({ isSyncing: false, error: getErrorMessage(error) });
      throw error;
    }
  },

  removeItem: async (itemId) => {
    set({ isSyncing: true, error: null });
    try {
      const cart = await cartService.removeItem(itemId);
      set({ ...cart, isSyncing: false });
    } catch (error) {
      set({ isSyncing: false, error: getErrorMessage(error) });
      throw error;
    }
  },

  clearCart: async () => {
    set({ isSyncing: true, error: null });
    try {
      const cart = await cartService.clearCart();
      set({ ...cart, isSyncing: false });
    } catch (error) {
      set({ isSyncing: false, error: getErrorMessage(error) });
      throw error;
    }
  },

  syncFromBackend: async () => {
    set({ isLoading: true, error: null });
    try {
      const cart = await cartService.getCart();
      set({ ...cart, isLoading: false });
    } catch (error) {
      set({ isLoading: false });
      if ((error as { response?: { status?: number } }).response?.status !== 401) {
        set({ error: getErrorMessage(error, 'Sepet yüklenemedi') });
      }
    }
  },

  reset: () => set({ ...emptyCart, isOpen: false, isLoading: false, isSyncing: false, error: null }),
}));

export function cartItemCount(items: CartItem[]): number {
  return items.reduce((sum, item) => sum + item.quantity, 0);
}
